import { format, parse, parseISO } from 'date-fns';
import { formatInTimeZone, toZonedTime, fromZonedTime } from 'date-fns-tz';

// Venue timezone constant
export const VENUE_TIMEZONE = 'America/Los_Angeles';
export const VENUE_TIMEZONE_ABBR = 'PT';
export const VENUE_TIMEZONE_NAME = 'Pacific Time';

/**
 * Parse a date string as Pacific Time
 * Used when reading dates from the database (which are stored without timezone info but represent PT)
 */
export function parseVenueTime(dateString: string): Date {
  // Remove 'Z' suffix if present - the times in DB are Pacific, not UTC
  const cleanDateString = dateString.replace('Z', '');
  // Parse the string as Pacific Time
  const parsed = parseISO(cleanDateString);
  // Convert from Pacific Time to UTC for JavaScript Date object
  return fromZonedTime(parsed, VENUE_TIMEZONE);
}

/**
 * Parse a date and time string as Pacific Time
 * Useful for parsing form inputs where date and time are separate
 */
export function parseVenueDateTime(date: string, time: string): Date {
  const dateTimeString = `${date}T${time}`;
  const parsed = parseISO(dateTimeString);
  // Convert from Pacific Time to UTC for JavaScript Date object
  return fromZonedTime(parsed, VENUE_TIMEZONE);
}

/**
 * Format a Date object for display in Pacific Time with PT indicator
 * @param date - JavaScript Date object (in UTC)
 * @param formatString - date-fns format string
 * @param includeTimezone - whether to include timezone indicator (default: true)
 */
export function formatVenueTime(
  date: Date | string,
  formatString: string,
  includeTimezone: boolean = true
): string {
  const dateObj = typeof date === 'string' ? parseVenueTime(date) : date;
  const formatted = formatInTimeZone(dateObj, VENUE_TIMEZONE, formatString);
  return includeTimezone ? `${formatted} ${VENUE_TIMEZONE_ABBR}` : formatted;
}

/**
 * Format a Date object for emails with full timezone name
 * @param date - JavaScript Date object (in UTC)
 * @param formatString - date-fns format string
 */
export function formatVenueTimeForEmail(
  date: Date | string,
  formatString: string = 'EEEE, MMMM d, yyyy \'at\' h:mm a'
): string {
  const dateObj = typeof date === 'string' ? parseVenueTime(date) : date;
  const formatted = formatInTimeZone(dateObj, VENUE_TIMEZONE, formatString);
  return `${formatted} ${VENUE_TIMEZONE_NAME}`;
}

/**
 * Get the current time in Pacific Time
 */
export function getCurrentVenueTime(): Date {
  return new Date();
}

/**
 * Convert a Date object to Pacific Time for storing in database
 * Returns an ISO string without timezone info that represents Pacific Time
 */
export function toVenueTimeString(date: Date): string {
  // Convert UTC to Pacific Time
  const zonedDate = toZonedTime(date, VENUE_TIMEZONE);
  // Format without timezone info for database storage
  return format(zonedDate, "yyyy-MM-dd'T'HH:mm:ss");
}

/**
 * Add timezone indicator to a formatted time string
 */
export function addTimezoneIndicator(
  formattedTime: string,
  useFullName: boolean = false
): string {
  const indicator = useFullName ? VENUE_TIMEZONE_NAME : VENUE_TIMEZONE_ABBR;
  return `${formattedTime} ${indicator}`;
}

/**
 * Common display formats with PT indicator
 */
export const VenueTimeFormats = {
  // Date only
  dateOnly: (date: Date | string) => formatVenueTime(date, 'MMM d, yyyy'),
  
  // Time only
  timeOnly: (date: Date | string) => formatVenueTime(date, 'h:mm a'),
  
  // Date and time
  dateTime: (date: Date | string) => formatVenueTime(date, 'MMM d, yyyy \'at\' h:mm a'),
  
  // Full date and time
  fullDateTime: (date: Date | string) => formatVenueTime(date, 'EEEE, MMMM d, yyyy \'at\' h:mm a'),
  
  // Compact format
  compact: (date: Date | string) => formatVenueTime(date, 'M/d/yy h:mm a'),
  
  // For emails (with full timezone name)
  email: (date: Date | string) => formatVenueTimeForEmail(date),
  
  // ISO format for forms (without timezone)
  forInput: (date: Date | string) => {
    const dateObj = typeof date === 'string' ? parseVenueTime(date) : date;
    return formatInTimeZone(dateObj, VENUE_TIMEZONE, "yyyy-MM-dd'T'HH:mm");
  },
  
  // Date for date input
  forDateInput: (date: Date | string) => {
    const dateObj = typeof date === 'string' ? parseVenueTime(date) : date;
    return formatInTimeZone(dateObj, VENUE_TIMEZONE, 'yyyy-MM-dd');
  },
  
  // Time for time input
  forTimeInput: (date: Date | string) => {
    const dateObj = typeof date === 'string' ? parseVenueTime(date) : date;
    return formatInTimeZone(dateObj, VENUE_TIMEZONE, 'HH:mm');
  }
};

/**
 * Helper to check if a date is in the past (in venue timezone)
 */
export function isVenueTimePast(date: Date | string): boolean {
  const dateObj = typeof date === 'string' ? parseVenueTime(date) : date;
  const now = new Date();
  return dateObj < now;
}

/**
 * Helper to check if a date is today (in venue timezone)
 */
export function isVenueTimeToday(date: Date | string): boolean {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const venueDate = formatInTimeZone(dateObj, VENUE_TIMEZONE, 'yyyy-MM-dd');
  const venueToday = formatInTimeZone(new Date(), VENUE_TIMEZONE, 'yyyy-MM-dd');
  return venueDate === venueToday;
}